import Alphabet from './Alphabet'
import {useAlphabet} from './AlphabetContext'
import {useSearch} from './SearchContext'
import {CogIcon} from '@heroicons/react/24/solid'
import {useCallback, useEffect, useState} from 'react'
import SelectWrapper from './SelectWrapper';

const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

export default function Search({ domainLabel, domainOptions, tokens, setTokens, children, alphabet }) {
  const {setCurrentLetter} = useAlphabet()
  const {searchText, setSearchText, domain, setDomain} = useSearch()
  const [isLoading, setIsLoading] = useState(false)
  const [displayNoResults, setDisplayNoResults] = useState(null)
  const [autocomplete, setAutocomplete] = useState([])

  const makeSearchApiCall = (data) => {
    const params = new URLSearchParams(data)
    const timer = setTimeout(() => setIsLoading(true), 500)
    const clearLoader = () => {
      clearTimeout(timer)
      setIsLoading(false)
    }

    setTokens([])
    setDisplayNoResults(false)

    fetch(Drupal.url('token-search') + '?' + params, {})
      .then(res => res.json())
      .then(data => {
        clearLoader()
        setTokens(data)

        if (data.length === 0) {
          setDisplayNoResults(true)
        }
      })
      .catch(error => {
        clearLoader()
        console.error('token-search fetch error', error)
      })
  }

  const makeAutocompleteApiCall = (q) => {
    const params = new URLSearchParams({
      q: q.trim()
    })

    if (!q.trim()) {
      closeAutocomplete()
      return
    }

    fetch(Drupal.url('token-autocomplete') + '?' + params, {})
      .then(res => res.json())
      .then(data => {
        setAutocomplete(data)
      })
      .catch(error => {
        console.error('autocomplete error', error)
      })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    setCurrentLetter(null)
    closeAutocomplete()

    makeSearchApiCall({
      search: searchText,
      domain: domain,
    })
  }

  const closeAutocomplete = () => setAutocomplete([])
  const closeAutocompleteOnEscape = (e) => {
    if (e.key === 'Escape') {
      closeAutocomplete()
    }
  }

  const handleAutocomplete = useCallback(
    debounce((q) => {
      makeAutocompleteApiCall(q)
    }, 150),
    []
  )

  const handleKeyUp = (e) => {
    // Pressing enter to submit the form passes empty value to the autocomplete handler and is will trigger closing
    // of autocomplete
    handleAutocomplete(e.key === 'Enter' ? '' : e.target.value)
  }

  const setSearchTextAndCloseAutocomplete = (text) => {
    setSearchText(text)
    closeAutocomplete()
  }

  useEffect(() => {
    document.addEventListener('keyup', closeAutocompleteOnEscape)

    return () => {
      document.removeEventListener('keyup', closeAutocompleteOnEscape)
    }
  }, [])

  return (
    <div>
      <form className="grid grid-cols-1 sm:grid-cols-12 gap-4" onSubmit={handleSubmit}>
        <div className="sm:col-span-5">
          <label htmlFor="search" className="block text-sm/6 font-medium text-gray-900 mb-2">
            {Drupal.t('Search')}
          </label>
          <input type="text" name="search" id="search" value={searchText} autoComplete="off" placeholder={Drupal.t('Search for tokens...')}
                 onChange={(e) => setSearchText(e.target.value)}
                 onKeyUp={handleKeyUp}
                 onBlur={() => setTimeout(() => closeAutocomplete(), 150)} />
          {autocomplete.length > 0 && (
            <div className="relative">
              <ul className="absolute w-full bg-white border-x border-b border-gray-300 rounded-b max-h-60 overflow-y-auto">
                {autocomplete.map((text, index) => (
                  <li key={index} className="cursor-pointer not-last:border-b not-last:border-b-gray-300 p-2 text-sm/6 font-medium text-gray-900 truncate hover:text-primary"
                      title={text} onClick={() => setSearchTextAndCloseAutocomplete(text)}>{ text }</li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="sm:col-span-5">
          <label htmlFor="domain" className="block text-sm/6 font-medium text-gray-900 mb-2">
            {domainLabel}
          </label>
          <SelectWrapper>
            <select id="domain" name="domain" value={domain}
                    onChange={(e) => setDomain(e.target.value)}>
              <option value="">{Drupal.t('- All -')}</option>
              {Object.entries(domainOptions).map(([value, text]) => (
                <option value={value} key={value}>{text}</option>
              ))}
            </select>
          </SelectWrapper>
        </div>
        <div className="sm:col-span-2 flex items-end">
          <button className="button form-submit grow">{Drupal.t('Search', {}, { context: 'Action' })}</button>
        </div>
      </form>

      <Alphabet alphabet={alphabet} makeSearchApiCall={makeSearchApiCall} />

      {isLoading && (
        <div className="flex items-center justify-center gap-2 text-gray-600 mb-4">
          <CogIcon className="size-6 animate-spin" />
          <span>{Drupal.t('Loading...')}</span>
        </div>
      )}

      {displayNoResults && (
        <div className="text-center text-sm text-gray-400 mb-4">{Drupal.t('No results')}</div>
      )}

      {displayNoResults === false && tokens.length !== 0 && (
        <div className="text-sm text-gray-400 mb-2">{Drupal.formatPlural(tokens.length, 'Shows 1 result.', 'Shows @count results.')}</div>
      )}

      <div>
        {children}
      </div>
    </div>
  )
}
